import React, { useState } from 'react';
import {
  X,
  FileText,
  Calendar,
  Tag,
  FolderOpen,
  Phone,
  PackageCheck,
  Mail,
  IdCard,
  HardDrive,
  ChevronDown,
} from 'lucide-react';
import { Arquivo } from '../../types/gerArquivos';
import {
  buildClienteDisplay,
  buildMaterialSummary,
  formatArquivoData,
  getOrigemBadgeClass,
  getOrigemLabel,
  shouldShowArquivoStatusBadge,
  resolvePedidoItemRef,
} from './utils/arquivoDisplay';
import { OrderItemTag, OrderTag } from '../ui';

const CATEGORIAS = {
  1: 'DTF TÊXTIL',
  2: 'DTF UV',
  3: 'SUBLIMÁTICA',
  4: 'CAMISETA',
} as const;

function getCategoriaColor(categoria: number): string {
  switch (categoria) {
    case 1:
      return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
    case 2:
      return 'bg-purple-500/20 text-purple-300 border-purple-500/30';
    case 3:
      return 'bg-green-500/20 text-green-300 border-green-500/30';
    case 4:
      return 'bg-orange-500/20 text-orange-300 border-orange-500/30';
    default:
      return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
  }
}

function getStatusColor(status: string) {
  switch (status) {
    case 'salvo':
      return 'text-green-400 bg-green-400/10 border-green-400/30';
    case 'em_edicao':
      return 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30';
    case 'finalizado':
      return 'text-blue-400 bg-blue-400/10 border-blue-400/30';
    default:
      return 'text-gray-400 bg-gray-400/10 border-gray-400/30';
  }
}

function getStatusText(status: string) {
  switch (status) {
    case 'salvo':
      return 'Salvo';
    case 'em_edicao':
      return 'Em edição';
    case 'finalizado':
      return 'Finalizado';
    default:
      return 'Desconhecido';
  }
}

function normalize(value: unknown): string {
  if (value == null) return '';
  return String(value).trim();
}

function formatOptional(value: unknown): string | null {
  const text = normalize(value);
  return text || null;
}

function formatBytes(value: unknown): string | null {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes <= 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function uniqueStrings(values: Array<string | null | undefined>): string[] {
  return [...new Set(values.map((v) => normalize(v)).filter(Boolean))];
}

function InfoRow({
  icon,
  label,
  value,
  mono = false,
}: {
  icon: React.ReactNode;
  label?: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  if (value == null || value === '' || value === false) return null;

  return (
    <div className="flex items-start gap-2 py-1 min-w-0">
      <span className="text-gray-500 flex-shrink-0 mt-0.5">{icon}</span>
      <div className="min-w-0 flex-1">
        {label && (
          <span className="text-[11px] text-gray-500 uppercase tracking-wide mr-1.5">
            {label}
          </span>
        )}
        <span
          className={`text-sm text-gray-100 break-words ${mono ? 'font-mono text-xs' : ''}`}
        >
          {value}
        </span>
      </div>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1">
      {children}
    </h4>
  );
}

interface ArquivoDetalhesModalProps {
  arquivo: Arquivo;
  onClose: () => void;
  onAbrirArquivo: (arquivo: Arquivo) => void;
}

export function ArquivoDetalhesModal({
  arquivo,
  onClose,
  onAbrirArquivo,
}: ArquivoDetalhesModalProps) {
  const [techOpen, setTechOpen] = useState(false);

  const formattedDate = formatArquivoData(arquivo.dataCriacao);
  const podeAbrir = arquivo.acoes?.podeAbrir ?? true;
  const motivoBloqueio = arquivo.acoes?.motivoBloqueio;
  const origemLabel = getOrigemLabel(arquivo);
  const pedidoRef = resolvePedidoItemRef(arquivo);
  const clienteDisplay = buildClienteDisplay(arquivo.cliente);
  const materialResumo = buildMaterialSummary(arquivo.materiais);
  const tamanho = formatBytes(arquivo.arquivoTamanho);

  const telefones = uniqueStrings([
    arquivo.cliente?.whatsapp,
    arquivo.cliWhatsapp,
    arquivo.cliente?.telefone,
    arquivo.cliente?.celular,
  ]);
  const cpfCnpj = formatOptional(arquivo.cliente?.cnpjCpf);
  const email = formatOptional(arquivo.cliente?.email);
  const temCliente =
    clienteDisplay.destaque !== 'Sem cliente' ||
    cpfCnpj ||
    email ||
    telefones.length > 0;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  React.useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 z-50 animate-in fade-in duration-200"
      onClick={handleBackdropClick}
    >
      <div className="bg-navy-800 rounded-t-xl sm:rounded-lg max-w-lg w-full max-h-[90vh] sm:max-h-[85vh] overflow-hidden shadow-2xl border border-navy-700 animate-in slide-in-from-bottom sm:zoom-in-95 duration-200">
        <div className="px-4 py-3 border-b border-navy-700 flex items-start justify-between gap-2 bg-navy-800/50">
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-semibold text-white flex items-center gap-2">
              <FileText className="w-4 h-4 text-blue-400 flex-shrink-0" />
              Detalhes
            </h3>
            <p
              className="mt-1 text-xs font-mono text-gray-400 break-all line-clamp-2"
              title={arquivo.arquivoNome}
            >
              {arquivo.arquivoNome}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-white hover:bg-navy-700 rounded-lg transition-all flex-shrink-0"
            title="Fechar (ESC)"
            aria-label="Fechar modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-4 py-3 space-y-3 overflow-y-auto max-h-[calc(90vh-120px)] sm:max-h-[calc(85vh-120px)]">
          <div className="flex flex-wrap gap-1.5">
            {origemLabel && (
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold border ${getOrigemBadgeClass(arquivo)}`}
              >
                {origemLabel}
              </span>
            )}
            {arquivo.prodCatItem && (
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold border ${getCategoriaColor(arquivo.prodCatItem)}`}
              >
                {CATEGORIAS[arquivo.prodCatItem]}
              </span>
            )}
            {shouldShowArquivoStatusBadge(arquivo.status) && (
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold border ${getStatusColor(arquivo.status)}`}
              >
                {getStatusText(arquivo.status)}
              </span>
            )}
            {pedidoRef &&
              (pedidoRef.oitId != null ? (
                <OrderItemTag
                  orderId={pedidoRef.orcId}
                  itemId={pedidoRef.oitId}
                  size="sm"
                />
              ) : (
                <OrderTag orderId={pedidoRef.orcId} size="sm" />
              ))}
            {materialResumo && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold border bg-navy-700/80 text-gray-200 border-navy-600">
                {materialResumo}
              </span>
            )}
          </div>

          <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-400">
            <span className="inline-flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {formattedDate}
            </span>
            {tamanho && (
              <span className="inline-flex items-center gap-1">
                <HardDrive className="w-3 h-3" />
                {tamanho}
              </span>
            )}
          </div>

          {!podeAbrir && motivoBloqueio && (
            <p className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-lg px-2.5 py-2">
              {motivoBloqueio}
            </p>
          )}

          {temCliente && (
            <section>
              <SectionTitle>Cliente</SectionTitle>
              <div className="rounded-lg border border-navy-700 bg-navy-900/40 px-3 py-2 space-y-0.5">
                <p className="text-sm font-medium text-white">{clienteDisplay.destaque}</p>
                {clienteDisplay.secundarios.length > 0 && (
                  <p className="text-xs text-gray-400">
                    {clienteDisplay.secundarios.join(' · ')}
                  </p>
                )}
                {cpfCnpj && (
                  <InfoRow
                    icon={<IdCard className="w-3.5 h-3.5" />}
                    value={cpfCnpj}
                  />
                )}
                {telefones.length > 0 && (
                  <InfoRow
                    icon={<Phone className="w-3.5 h-3.5" />}
                    value={telefones.join(' · ')}
                  />
                )}
                {email && (
                  <InfoRow icon={<Mail className="w-3.5 h-3.5" />} value={email} />
                )}
              </div>
            </section>
          )}

          {arquivo.observacoes?.trim() && (
            <section>
              <SectionTitle>Observações</SectionTitle>
              <p className="text-sm text-gray-300 whitespace-pre-wrap leading-relaxed rounded-lg border border-navy-700 bg-navy-900/40 px-3 py-2">
                {arquivo.observacoes}
              </p>
            </section>
          )}

          <section>
            <button
              type="button"
              onClick={() => setTechOpen((open) => !open)}
              className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-gray-500 hover:text-gray-300 transition-colors w-full"
              aria-expanded={techOpen}
            >
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform ${techOpen ? 'rotate-180' : ''}`}
              />
              Detalhes técnicos
            </button>
            {techOpen && (
              <div className="mt-1.5 rounded-lg border border-navy-700 bg-navy-900/40 px-3 py-1.5 space-y-0">
                <InfoRow icon={<Tag className="w-3.5 h-3.5" />} label="ID" value={arquivo.id} mono />
                {formatOptional(arquivo.hashArquivo) && (
                  <InfoRow
                    icon={<Tag className="w-3.5 h-3.5" />}
                    label="Hash"
                    value={formatOptional(arquivo.hashArquivo)}
                    mono
                  />
                )}
                {arquivo.arquivoCaminho && (
                  <InfoRow
                    icon={<FolderOpen className="w-3.5 h-3.5" />}
                    label="Caminho"
                    value={arquivo.arquivoCaminho}
                    mono
                  />
                )}
              </div>
            )}
          </section>
        </div>

        <div className="px-4 py-3 border-t border-navy-700 bg-navy-800/50 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 bg-navy-700 hover:bg-navy-600 text-gray-300 hover:text-white rounded-lg text-sm transition-all"
          >
            Fechar
          </button>
          {podeAbrir ? (
            <button
              onClick={() => {
                onAbrirArquivo(arquivo);
                onClose();
              }}
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-all flex items-center gap-1.5"
            >
              <PackageCheck className="w-4 h-4" />
              Abrir arquivo
            </button>
          ) : (
            <button
              disabled
              title={motivoBloqueio ?? 'Abertura não permitida'}
              className="px-3 py-1.5 bg-navy-700 text-gray-500 rounded-lg text-sm cursor-not-allowed flex items-center gap-1.5 opacity-60"
            >
              <PackageCheck className="w-4 h-4" />
              Bloqueado
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
